import { View, ViewStyle } from 'react-native';
import { GhostButton } from 'src/editor-v3/components/buttons/ghost-button/GhostButton';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { styles } from './BottomSheetGroupTabs.styles';
import TextLato from '../text/text-lato/TextLato';
import NumberBadge from '../number-badge/NumberBadge';
import { background } from 'src/theme/colors';
import { observer } from 'mobx-react-lite';
import { useBottomSheetGroupTabsLogic } from './BottomSheetGroupTabs.logic';
import { ICON_NAMES } from '../icon/icon.helper';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { bottomSheetTestID, groupTabTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { Icon } from '../icon/Icon';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { useFocusableGroups } from 'src/hooks/useFocusableGroup';

export const BottomSheetGroupTabs = observer(({ bsStyle }: { bsStyle: ViewStyle }) => {
  const { i18n, groups, onPressGroup, getGroupTitle, addMoreProductsHandler, isUploading, closeBottomSheet } =
    useBottomSheetGroupTabsLogic();

  useFocusableGroups([FocusGroups.BOTTOM_SHEET]);

  return (
    <BottomSheetScrollView contentContainerStyle={{ ...bsStyle, ...styles.container }}>
      <View style={styles.header}>
        <TextLato style={styles.title}>{i18n.t('groups:all-products-project-description')}</TextLato>
        <CircularButton
          onPress={async () => closeBottomSheet()}
          accessible={true}
          accessibilityLabel={i18n.t('actions:close_x', { x: '' })}
          accessibilityRole="button"
          iconName={ICON_NAMES.Close}
          iconStyle={styles.closeIcon}
          size={CIRCULAR_BUTTON_SIZES.SMALL}
          type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
          testID={bottomSheetTestID(TestIDs.CLOSE)}
          focusId={bottomSheetTestID(TestIDs.CLOSE)}
          focusOrder={0}
          focusGroup={FocusGroups.BOTTOM_SHEET}
        />
      </View>

      <View style={styles.groupsListContainer}>
        {groups.map((group, index) => {
          const isWarningVisible = group.isMissingMinProducts;
          const title = getGroupTitle(group);
          return (
            <PressableHovered
              style={[styles.groupButton]}
              onPress={() => onPressGroup(group.id)}
              key={`group-tab-selector-id-${group.id}`}
              testID={groupTabTestID(group.code)}
              focusId={groupTabTestID(group.code)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={index + 1}
              accessibilityLabel={title}
              accessibilityRole="button"
            >
              <View style={styles.groupTab}>
                <View style={styles.groupTabLeftContent}>
                  <TextLato style={styles.groupTitle}>{title}</TextLato>
                  {isWarningVisible && (
                    <TextLato style={styles.warningText}>
                      {i18n.t(`groups:${group.code}-warning`, { photos: group.minItems })}
                    </TextLato>
                  )}
                </View>

                <View style={styles.groupTabRightContent}>
                  <NumberBadge
                    value={group.totalProducts}
                    backgroundColor={background.backgroundColorDefault}
                    color={isWarningVisible ? styles.numberBadgeWarning.color : styles.numberBadge.color}
                    containerStyle={styles.numberBadgeContainer}
                    showWarning={isWarningVisible}
                  />
                  <Icon
                    name={ICON_NAMES.ArrowRight1}
                    color={styles.arrowIcon.color}
                    width={styles.arrowIcon.width}
                    height={styles.arrowIcon.height}
                  />
                </View>
              </View>
            </PressableHovered>
          );
        })}
      </View>
      <GhostButton
        text={i18n.t('group-header:add-more-products')}
        onPress={addMoreProductsHandler}
        focusId={`group-tab-selector-add-more-button`}
        focusOrder={groups.length + 1}
        focusGroup={FocusGroups.BOTTOM_SHEET}
        size={BUTTON_SIZES.REGULAR}
        type={BUTTON_TYPES.ICON_LEFT}
        iconName={ICON_NAMES.Plus}
        layout={BUTTON_LAYOUTS.CENTER}
        loading={false}
        disabled={isUploading}
      />
    </BottomSheetScrollView>
  );
});

BottomSheetGroupTabs.displayName = 'BottomSheetGroupTabs';
